import * as Sentry from '@sentry/react';
import type { ErrorInfo } from 'react';
import {
  monolithTracePropagationTargets,
  replayPrivacyOptions,
  sampleRate,
  scrubBreadcrumb,
  scrubLog,
  scrubRecordingEvent,
  scrubSpan,
  scrubUrlsIntegration,
} from 'sentry-privacy';

import {
  GIT_COMMIT_SHA,
  SENTRY_DSN,
  SENTRY_LOGS_ENABLED,
  SENTRY_PROFILES_SAMPLE_RATE,
  SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  SENTRY_TRACES_SAMPLE_RATE,
} from '@/config/runtime-env';
import { domainMeta } from '@/utils/domain-meta';

export function reportBoundaryError(boundary: string) {
  return (error: unknown, info: ErrorInfo): void => {
    Sentry.captureReactException(error, info, {
      captureContext: { tags: { boundary } },
    });
  };
}

/**
 * Browser error reporting, tracing, replay, profiling and logs for the Source
 * dashboard.
 *
 * Dormant unless VITE_SENTRY_DSN is baked into the build. That gate is the whole
 * switch: a local `npm run dev` and a Vitest run both leave it unset and
 * therefore start no client at all.
 *
 * One bundle serves every tenant, so the DSN is a build-time constant while the
 * environment and tenant are read from the hostname at load.
 */

export function initSentry(): void {
  if (!SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: domainMeta.environment,
    // Ties an event to the build that produced it, and to the source maps
    // uploaded under the same name.
    release: GIT_COMMIT_SHA || undefined,
    tracesSampleRate: sampleRate(SENTRY_TRACES_SAMPLE_RATE, 0.01),
    // Relative to the sampled traces, not to all page loads. Inert until the
    // frontend worker serves `Document-Policy: js-profiling`.
    profilesSampleRate: sampleRate(SENTRY_PROFILES_SAMPLE_RATE, 1),
    // Nothing is recorded routinely. A session that produces an error is
    // recorded whatever this is, which is the case worth watching back.
    replaysSessionSampleRate: sampleRate(SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0),
    replaysOnErrorSampleRate: sampleRate(
      SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1,
    ),
    tracePropagationTargets: monolithTracePropagationTargets(
      domainMeta.monolithOrigin,
      Sentry.getActiveSpan,
    ),
    // `scrubBreadcrumb` drops console breadcrumbs, and `scrubLog` keeps a
    // console log's text but not its arguments: what the app logs includes
    // `ApiError.body`, which is what a candidate typed. On unless the build
    // sets the variable to "false"; turning it off takes a rebuild.
    enableLogs: SENTRY_LOGS_ENABLED !== 'false',
    beforeSendLog: scrubLog,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // A replay carries whatever was on screen, and these pages carry campaign
      // spend and applicant counts. Masked by default; a component opts its own
      // subtree out.
      Sentry.replayIntegration({
        // Scrubs the navigation and network entries the recording holds.
        //
        // Known limit, accepted: the page URL, query values such as search
        // terms included, reaches the replay verbatim through rrweb's meta
        // event, which passes no SDK hook. So does the `href` of a link with
        // no query string. On-page text stays masked.
        beforeAddRecordingEvent: scrubRecordingEvent,
        ...replayPrivacyOptions,
      }),
      // Fed by `utils/sentry-user`. The flags on an event are the ones the
      // session was evaluated under, which is usually the first question asked
      // of an issue that only some accounts see.
      Sentry.featureFlagsIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Page and request URLs carry credentials and search terms.
      scrubUrlsIntegration(),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    // Standalone spans, INP among them, reach no event processor.
    beforeSendSpan: scrubSpan,
    // No email, no name, no IP address. A recruiter's uuid is what "users
    // affected" counts, and it is attached separately.
    sendDefaultPii: false,
  });

  Sentry.setTag('tenant', domainMeta.tenant);
}
